import React, { useEffect, useState } from 'react';
import { initFlowbite } from 'flowbite';

interface HeaderProps {
  hideOnMobileUntilScroll?: boolean;
}

const Header: React.FC<HeaderProps> = ({ hideOnMobileUntilScroll = false }) => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMegaMenuOpen, setIsMegaMenuOpen] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  useEffect(() => {
    initFlowbite();

    const handleScroll = () => {
      const scrollPosition = window.scrollY;
      setIsScrolled(scrollPosition > 100);
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const megaMenu = document.getElementById('mega-menu');
    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.attributeName === 'class') {
          const isHidden = (mutation.target as HTMLElement).classList.contains('hidden');
          if (isHidden) {
            setIsClosing(true);
            setTimeout(() => {
              setIsMegaMenuOpen(false);
              setIsClosing(false);
            }, 500);
          } else {
            setIsMegaMenuOpen(true);
            requestAnimationFrame(() => {
              setIsClosing(false);
            });
          }
        }
      });
    });

    if (megaMenu) {
      observer.observe(megaMenu, { attributes: true });
    }

    return () => observer.disconnect();
  }, []);

  return (
    <>
      {(isMegaMenuOpen || isClosing) && (
        <div
          className={`fixed inset-0 z-[998] transition-all duration-500 ease-in-out ${
            isClosing || !isMegaMenuOpen
              ? 'bg-background/0 opacity-0 backdrop-blur-none'
              : 'bg-background/50 opacity-100 backdrop-blur'
          }`}
          aria-hidden="true"
        />
      )}
      <header
        className={`fixed inset-x-0 top-0 z-[999] transition-transform duration-300 ${
          hideOnMobileUntilScroll && !isScrolled
            ? '-translate-y-full sm:block sm:translate-y-0'
            : 'translate-y-0'
        }`}
      >
        <nav
          className={`border-gray-200 p-4 font-sans transition-all duration-200 dark:bg-gray-800 lg:px-6 ${
            isScrolled
              ? 'bg-background shadow-[0_2px_15px_-5px_rgba(0,0,0,0.10)]'
              : 'bg-background/95'
          }`}
        >
          <div className="mx-auto flex max-w-screen-xl flex-wrap items-center justify-between pr-2">
            <a href="/" className="flex items-center">
              <img
                src="/assets/icon.webp"
                className="mr-3 aspect-square h-8 object-contain sm:h-10"
                alt="Belgian Game Industry Logo"
              />
              <span className="hidden self-center whitespace-nowrap font-sans text-xl font-light text-gray-600 dark:text-white lg:inline">
                Belgian Game Industry
              </span>
            </a>

            {/* Mobile menu button */}
            <button
              data-collapse-toggle="mobile-menu-2"
              type="button"
              className="ml-1 inline-flex items-center rounded-lg p-2 text-sm text-gray-500 hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-gray-200 dark:text-gray-400 dark:hover:bg-gray-700 dark:focus:ring-gray-600 sm:hidden"
              aria-controls="mobile-menu-2"
              aria-expanded="false"
            >
              <span className="sr-only">Open main menu</span>
              <svg
                className="size-6"
                fill="currentColor"
                viewBox="0 0 20 20"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  d="M3 5a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 10a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM3 15a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1z"
                  clipRule="evenodd"
                ></path>
              </svg>
              <svg
                className="hidden size-6"
                fill="currentColor"
                viewBox="0 0 20 20"
                xmlns="http://www.w3.org/2000/svg"
              >
                <path
                  fillRule="evenodd"
                  d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                  clipRule="evenodd"
                ></path>
              </svg>
            </button>

            {/* Desktop/Mobile menu */}
            <div
              className="hidden w-full sm:flex sm:flex-1 sm:items-center sm:justify-end sm:pr-4"
              id="mobile-menu-2"
            >
              <ul className="flex flex-col font-sans font-medium sm:flex-row sm:items-center sm:space-x-8">
                <li>
                  <a
                    href="https://gameindustry.be/"
                    className="block py-2 text-gray-700 hover:text-primary-600 sm:inline-block sm:p-0 sm:font-light"
                  >
                    Companies
                  </a>
                </li>
                <li>
                  <a
                    href="https://gamejobs.be"
                    className="block py-2 text-gray-700 hover:text-primary-600 sm:inline-block sm:p-0 sm:px-3 sm:font-light"
                  >
                    Game Jobs
                  </a>
                </li>
                <li>
                  {/* Mobile version - no dropdown trigger */}
                  <button className="block w-full py-2 text-left text-primary-600 hover:text-primary-700 sm:hidden sm:px-3">
                    Game Mentors
                  </button>
                  {/* Desktop version - with mega menu */}
                  <button
                    id="mega-menu-button"
                    data-dropdown-toggle="mega-menu"
                    className="hidden w-full px-3 py-2 text-left text-primary-600 hover:text-primary-700 sm:inline-block sm:w-auto sm:p-0 sm:px-3"
                    aria-current="page"
                  >
                    Game Mentors{' '}
                    <svg
                      className="ml-1 inline-block size-4"
                      fill="currentColor"
                      viewBox="0 0 20 20"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        fillRule="evenodd"
                        d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z"
                        clipRule="evenodd"
                      ></path>
                    </svg>
                  </button>
                  {/* Mobile menu items - always visible when menu is open */}
                  <div className="sm:hidden">
                    <a href="/" className="block py-2 pl-3 text-gray-700 hover:text-primary-600">
                      Overview
                    </a>
                    <a
                      href="/mentors"
                      className="block py-2 pl-3 text-gray-700 hover:text-primary-600"
                    >
                      Find Mentors
                    </a>
                  </div>
                  {/* Desktop mega menu */}
                  <div
                    id="mega-menu"
                    className="absolute right-0 z-10 mt-2 hidden w-80 border border-gray-100 bg-white p-2 shadow-md dark:border-gray-700 dark:bg-gray-700 sm:rounded-lg"
                  >
                    <div className="rounded-lg bg-white p-1 text-gray-900 dark:bg-gray-800 dark:text-white">
                      <ul className="space-y-2">
                        <li>
                          <a
                            href="/"
                            className="inline-flex w-full items-center rounded-lg p-2 hover:bg-gray-50 dark:hover:bg-gray-700"
                          >
                            <div className="mr-3 rounded-lg bg-white p-1.5 shadow dark:bg-gray-700">
                              <svg
                                className="size-5 text-gray-600 dark:text-gray-400"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z" />
                              </svg>
                            </div>
                            <div>
                              <div className="font-sans font-medium text-gray-600">Overview</div>
                              <div className="font-sans text-sm font-light text-gray-500 dark:text-gray-400">
                                Learn about Game Mentors
                              </div>
                            </div>
                          </a>
                        </li>
                        <li>
                          <a
                            href="/mentors"
                            className="inline-flex w-full items-center rounded-lg p-2 hover:bg-gray-50 dark:hover:bg-gray-700"
                          >
                            <div className="mr-3 rounded-lg bg-white p-1.5 shadow dark:bg-gray-700">
                              <svg
                                className="size-5 text-gray-600 dark:text-gray-400"
                                fill="currentColor"
                                viewBox="0 0 20 20"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path d="M13 6a3 3 0 11-6 0 3 3 0 016 0zM18 8a2 2 0 11-4 0 2 2 0 014 0zM14 15a4 4 0 00-8 0v3h8v-3zM6 8a2 2 0 11-4 0 2 2 0 014 0zM16 18v-3a5.972 5.972 0 00-.75-2.906A3.005 3.005 0 0119 15v3h-3zM4.75 12.094A5.973 5.973 0 004 15v3H1v-3a3 3 0 013.75-2.906z" />
                              </svg>
                            </div>
                            <div>
                              <div className="font-sans font-medium text-gray-600">
                                Find Mentors
                              </div>
                              <div className="font-sans text-sm font-light text-gray-500 dark:text-gray-400">
                                Browse all mentors
                              </div>
                            </div>
                          </a>
                        </li>
                      </ul>
                    </div>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </nav>
      </header>
    </>
  );
};

export default Header;
